/**
 * Her side of voice (D29): one Room, her own `child-<id>`, which holds her and
 * him only. She joins it when her sitting starts, so his muted mic is already
 * there, and she hears him the moment he takes her.
 *
 * **Her mic is hot only while she is taken.** `taken` and `released` come
 * over her SSE stream (the header passes them in as `setTaken`). Outside a
 * take her mic is not published at all: not muted, absent. On a take it is
 * captured and published; on a release it is unpublished and the capture
 * stopped, so the browser's own mic light goes out too.
 *
 * The server holds the same line: her token carries `canPublish` only while
 * she is taken, and a take or release updates it on her live connection
 * (api/voice.py). So a take waits for that permission before publishing (a
 * fresh token if it has not arrived in a few seconds), and a release that
 * never reached her over SSE still ends her track.
 */
import {
  DisconnectReason,
  type LocalAudioTrack,
  type RemoteAudioTrack,
  type RemoteTrack,
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
} from "livekit-client";

import { voiceApi, voiceOff } from "./api";
import { MIC_WORDS, PUBLISH, backoff, capture, micProblem, roomOptions, saveMic, savedMic } from "./audio";
import { type Meter, meter, resumeAudio } from "./meter";

export interface ChildVoiceView {
  /** off: no voice on this server; replaced: her other tab has it */
  conn: "off" | "connecting" | "connected" | "reconnecting" | "retrying" | "replaced";
  taken: boolean;
  /** her mic: absent, being started, live (published and heard), or a problem she can fix */
  mic: "off" | "starting" | "live" | "problem";
  problem: string | null;
  /** he is in her room */
  tutorHere: boolean;
  audioBlocked: boolean;
  devices: { id: string; label: string }[];
  device: string | undefined;
}

const PERMISSION_WAIT_MS = 4_000;

export class ChildVoice {
  private room: Room | null = null;
  private track: LocalAudioTrack | null = null;
  private myMeter: Meter | null = null;
  private hisMeter: Meter | null = null;
  private els = new Set<HTMLMediaElement>();
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private permTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private busy: Promise<void> = Promise.resolve();
  private listeners = new Set<() => void>();
  private view: ChildVoiceView = {
    conn: "connecting",
    taken: false,
    mic: "off",
    problem: null,
    tutorHere: false,
    audioBlocked: false,
    devices: [],
    device: savedMic(),
  };

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ChildVoiceView => this.view;

  private set(patch: Partial<ChildVoiceView>): void {
    this.view = { ...this.view, ...patch };
    for (const f of this.listeners) f();
  }

  levels(): { me: number; him: number } {
    return { me: this.myMeter?.level() ?? 0, him: this.hisMeter?.level() ?? 0 };
  }

  /* ------------------------------------------------------------ what her screen tells it */

  start(): void {
    void this.connect();
  }

  setTaken(taken: boolean): void {
    if (this.view.taken === taken) return;
    this.set({ taken });
    this.serial(() => (taken ? this.publish() : this.unpublish()));
  }

  retryMic(): void {
    this.unlockAudio();
    this.set({ problem: null });
    if (this.view.taken) this.serial(() => this.publish());
  }

  async setDevice(id: string): Promise<void> {
    saveMic(id);
    this.set({ device: id });
    if (this.track) await this.room?.switchActiveDevice("audioinput", id).catch(() => undefined);
  }

  unlockAudio(): void {
    resumeAudio();
    void this.room?.startAudio().catch(() => undefined);
    this.set({ audioBlocked: false });
  }

  destroy(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.permTimer) clearTimeout(this.permTimer);
    this.dropTrack();
    this.dropAudio();
    void this.room?.disconnect();
    this.room = null;
    this.listeners.clear();
  }

  private serial(f: () => Promise<void>): void {
    this.busy = this.busy.then(f).catch((e) => console.warn("voice", e));
  }

  /* ------------------------------------------------------------ the room */

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.set({ conn: this.attempt ? "retrying" : "connecting" });
    let room: Room | null = null;
    try {
      const t = await voiceApi.childToken();
      if (this.stopped) return;
      room = new Room(roomOptions());
      this.wire(room);
      this.room = room;
      await room.connect(t.url, t.token, { autoSubscribe: true });
      if (this.stopped) {
        await room.disconnect();
        return;
      }
      this.attempt = 0;
      this.set({ conn: "connected", tutorHere: room.remoteParticipants.has("tutor"), audioBlocked: !room.canPlaybackAudio });
      void this.devices();
      if (this.view.taken) this.serial(() => this.publish());
    } catch (err) {
      if (voiceOff(err)) {
        this.set({ conn: "off" });
        return;
      }
      if (room && this.room === room) this.room = null;
      void room?.disconnect();
      this.retry();
    }
  }

  private retry(): void {
    if (this.stopped || this.timer) return;
    this.set({ conn: "retrying" });
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.connect();
    }, backoff(this.attempt++));
  }

  private wire(room: Room): void {
    const here = () => this.set({ tutorHere: room.remoteParticipants.has("tutor") });
    room
      .on(RoomEvent.Reconnecting, () => this.set({ conn: "reconnecting" }))
      .on(RoomEvent.Reconnected, () => this.set({ conn: "connected" }))
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        if (this.room !== room) return;
        this.room = null;
        this.dropTrack();
        this.dropAudio();
        if (this.stopped) return;
        if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
          this.set({ conn: "replaced", mic: "off", tutorHere: false });
          return;
        }
        this.set({ mic: "off", tutorHere: false });
        this.retry();
      })
      .on(RoomEvent.ParticipantConnected, here)
      .on(RoomEvent.ParticipantDisconnected, () => {
        this.dropAudio();
        here();
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind !== Track.Kind.Audio) return;
        this.play(track as RemoteAudioTrack);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        for (const el of track.detach()) {
          el.remove();
          this.els.delete(el);
        }
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => this.set({ audioBlocked: !room.canPlaybackAudio }))
      .on(RoomEvent.ParticipantPermissionsChanged, () => {
        const can = !!room.localParticipant.permissions?.canPublish;
        if (can && this.view.taken && !this.track) this.serial(() => this.publish());
        if (!can && this.track) this.serial(() => this.unpublish());
      })
      .on(RoomEvent.LocalTrackUnpublished, () => {
        // the server revoked it (a release), or we did: either way the capture ends
        this.dropTrack();
        this.set({ mic: "off" });
      })
      .on(RoomEvent.MediaDevicesError, (err: Error) => this.set({ mic: "problem", problem: MIC_WORDS[micProblem(err)] }));
  }

  /* ------------------------------------------------------------ her mic */

  private async publish(): Promise<void> {
    const room = this.room;
    if (!this.view.taken || this.track || !room || this.view.conn !== "connected") return;
    if (!room.localParticipant.permissions?.canPublish) {
      // the server grants it with the take; if it has not reached this connection, a fresh token carries it
      this.set({ mic: "starting" });
      if (!this.permTimer) {
        this.permTimer = setTimeout(() => {
          this.permTimer = null;
          if (this.view.taken && !this.track && this.room === room && !room.localParticipant.permissions?.canPublish) {
            void room.disconnect();
          }
        }, PERMISSION_WAIT_MS);
      }
      return;
    }
    this.set({ mic: "starting", problem: null });
    let t: LocalAudioTrack;
    try {
      t = await createLocalAudioTrack(capture(this.view.device));
    } catch (err) {
      this.set({ mic: "problem", problem: MIC_WORDS[micProblem(err)] });
      return;
    }
    if (!this.view.taken || this.room !== room) {
      t.stop();
      return;
    }
    try {
      await room.localParticipant.publishTrack(t, PUBLISH);
    } catch {
      t.stop();
      this.set({ mic: "problem", problem: MIC_WORDS.other });
      return;
    }
    this.track = t;
    this.myMeter?.stop();
    this.myMeter = meter(t.mediaStreamTrack);
    void this.devices();
    this.set({ mic: "live" });
    if (!this.view.taken) await this.unpublish(); // released while we were starting
  }

  private async unpublish(): Promise<void> {
    if (this.permTimer) {
      clearTimeout(this.permTimer);
      this.permTimer = null;
    }
    const t = this.track;
    this.track = null;
    if (t && this.room) await this.room.localParticipant.unpublishTrack(t, true).catch(() => undefined);
    t?.stop();
    this.myMeter?.stop();
    this.myMeter = null;
    this.set({ mic: "off" });
  }

  private dropTrack(): void {
    this.track?.stop();
    this.track = null;
    this.myMeter?.stop();
    this.myMeter = null;
  }

  private play(track: RemoteAudioTrack): void {
    const el = track.attach();
    el.dataset.voiceSink = "child";
    el.hidden = true;
    document.body.appendChild(el);
    this.els.add(el);
    this.hisMeter?.stop();
    this.hisMeter = meter(track.mediaStreamTrack);
  }

  private dropAudio(): void {
    for (const el of this.els) el.remove();
    this.els.clear();
    this.hisMeter?.stop();
    this.hisMeter = null;
  }

  private async devices(): Promise<void> {
    try {
      const all = await Room.getLocalDevices("audioinput", false);
      this.set({ devices: all.filter((d) => d.deviceId).map((d) => ({ id: d.deviceId, label: d.label || "mikrofonas" })) });
    } catch {
      /* the default device is used */
    }
  }

  /** The dev-only handle the wire test reads (`e2e/voice-wire.spec.ts`). */
  get roomForTests(): Room | null {
    return this.room;
  }
}

export function exposeForTests(v: ChildVoice): void {
  if (import.meta.env.DEV) (window as unknown as { __ugisChildVoice?: ChildVoice }).__ugisChildVoice = v;
}
